import "./EditableFilename.scss";

import cx from "classnames";
import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { useUpdateDocumentMutation } from "services/documents-api";

import type { DocumentType } from "../DocumentTypes";

const MAX_FILENAME_LENGTH = 80;

type EditableFilenameProps = {
  document: DocumentType;
  className?: string;
};

const EditableFilename = ({ document, className }: EditableFilenameProps) => {
  const [updateDocument] = useUpdateDocumentMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(document.original_filename);
  const [isHovered, setIsHovered] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // reset value when document changes
  useEffect(() => {
    setValue(document.original_filename);
  }, [document.original_filename]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      KaboodleToaster.error("Filename cannot be empty");
      setValue(document.original_filename);
      setIsEditing(false);
      return;
    }

    if (trimmedValue === document.original_filename) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await updateDocument({
        documentId: document.id,
        data: {
          original_filename: trimmedValue,
        },
      }).unwrap();

      setIsEditing(false);
      KaboodleToaster.success("Document name updated successfully");
    } catch (error) {
      console.error("Failed to update document name:", error);
      KaboodleToaster.error("Failed to update document name");
      setValue(document.original_filename);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [
    document.id,
    document.original_filename,
    value,
    updateDocument,
    isSaving,
  ]);

  const handleCancel = useCallback(() => {
    setValue(document.original_filename);
    setIsEditing(false);
  }, [document.original_filename]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue.length <= MAX_FILENAME_LENGTH) {
        setValue(newValue);
      }
    },
    [],
  );

  // focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const shouldShowTooltip = document.original_filename.length > 50;

  const filenameContent = (
    <div
      className={cx("editable-filename", className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...(shouldShowTooltip && {
        "data-tooltip-id": "document-name-tooltip",
        "data-tooltip-content": document.original_filename,
        "data-tooltip-place": "top",
      })}
    >
      <span className="editable-filename__text">
        {document.original_filename}
      </span>
      <button
        className={cx("editable-filename__edit-button", {
          "editable-filename__edit-button--visible": isHovered,
        })}
        onClick={handleEdit}
        title="Edit filename"
      >
        <span className="material-icons">edit</span>
      </button>
    </div>
  );

  if (isEditing) {
    return (
      <div
        className={cx(
          "editable-filename",
          "editable-filename--editing",
          className,
        )}
        // allow keyboard events to pass through to the input
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onBlur={handleSave}
          // allow keyboard events to pass through to the input
          onKeyDown={(e) => {
            e.stopPropagation();
            handleKeyDown(e);
          }}
          className="editable-filename__input"
          disabled={isSaving}
          maxLength={MAX_FILENAME_LENGTH}
        />
        {isSaving && (
          <div className="editable-filename__saving">
            <span className="material-icons">hourglass_empty</span>
          </div>
        )}
      </div>
    );
  }

  return filenameContent;
};

export default EditableFilename;
